<template>
	<section class="parameter-manage">
		<p class="fa-hint">
			两级结构：顶级 = 参数维度（如「电压」），子级 = 可选项（如「12V」）。商品在工艺参数中勾选维度后，报价页按维度顺序拼接，例如
			<strong>{{ comboExample || '12V 3000K' }}</strong>。
		</p>
		<div class="pm-toolbar">
			<div class="pm-toolbar__filters">
				<el-input
					v-model="search"
					class="pm-search"
					clearable
					prefix-icon="el-icon-search"
					placeholder="搜索名称"
					@keyup.enter.native="onSearch"
					@clear="onSearch" />
				<el-button size="small" @click="setExpandAll(true)">全部展开</el-button>
				<el-button size="small" @click="setExpandAll(false)">全部收起</el-button>
			</div>
			<div class="pm-toolbar__actions">
				<el-button v-if="canAdd" type="primary" icon="el-icon-plus" class="pm-btn-primary" @click="openCreate(0)">添加维度</el-button>
			</div>
		</div>

		<el-table
			ref="paramTable"
			v-loading="loading"
			:data="treeList"
			stripe
			row-key="id"
			:tree-props="{ children: 'children' }"
			:default-expand-all="true"
			:row-class-name="treeRowClass"
			class="pm-table pm-tree-table"
			style="width: 100%"
			cell-class-name="pm-cell"
			header-cell-class-name="pm-header-cell">
			<el-table-column align="left" min-width="220" label="名称" show-overflow-tooltip>
				<template slot-scope="scope">
					<span class="tree-name" :class="{ 'is-root': Number(scope.row.pid || 0) === 0 }">
						{{ scope.row.name || scope.row.name_display || '—' }}
					</span>
				</template>
			</el-table-column>
			<el-table-column align="center" width="60" label="ID" prop="id" />
			<el-table-column align="center" width="72" prop="level_text" label="层级" />
			<el-table-column align="left" min-width="180" label="可选值预览" show-overflow-tooltip>
				<template slot-scope="scope">
					<span v-if="Number(scope.row.pid || 0) === 0">{{ scope.row.options_preview || '—' }}</span>
					<span v-else class="muted">—</span>
				</template>
			</el-table-column>
			<el-table-column align="center" width="72" prop="must_text" label="必填" />
			<el-table-column align="center" width="80" prop="type_text" label="类型" />
			<el-table-column align="center" width="88" label="计价">
				<template slot-scope="scope">{{ scope.row.price_text || formatPriceCell(scope.row) }}</template>
			</el-table-column>
			<el-table-column align="left" min-width="100" prop="remarks" label="备注" show-overflow-tooltip />
			<el-table-column align="center" width="72" prop="weigh" label="排序" />
			<el-table-column align="center" width="168" label="创建时间">
				<template slot-scope="scope"><span class="pm-time">{{ formatTime(scope.row.createtime) }}</span></template>
			</el-table-column>
			<el-table-column align="center" width="168" label="操作" fixed="right">
				<template slot-scope="scope">
					<div class="row-actions">
						<el-button v-if="canAdd && Number(scope.row.pid || 0) === 0" type="text" class="row-action-btn" @click="openCreate(scope.row.id)">添加选项</el-button>
						<el-button v-if="canEdit" type="text" icon="el-icon-edit" class="row-action-btn" @click="openEdit(scope.row.id)" />
						<el-popconfirm v-if="canDel" @onConfirm="delRow(scope.row.id)"
							confirmButtonType="danger" confirmButtonText="删除" title="确定删除？若有子项请先删除子项。">
							<el-button slot="reference" type="text" icon="el-icon-delete" class="row-action-btn row-action-btn--danger" />
						</el-popconfirm>
					</div>
				</template>
			</el-table-column>
		</el-table>

		<el-dialog :title="dialogTitle" :visible.sync="formVisible" width="640px" append-to-body @close="resetForm">
			<el-form ref="paramForm" :model="form" :rules="formRules" label-width="96px">
				<el-form-item label="名称" prop="name">
					<el-input v-model="form.name" :placeholder="form.pid ? '如 12V、3000K' : '如 电压、灯光色温'" />
				</el-form-item>
				<el-form-item label="上级">
					<el-select v-model="form.pid" style="width: 100%" :disabled="!!form.id">
						<el-option :value="0" label="顶级（参数维度）" />
						<el-option v-for="p in parentOptions" :key="p.id" :label="p.label || p.name" :value="p.id" />
					</el-select>
				</el-form-item>
				<el-form-item v-if="Number(form.pid) === 0" label="维度必填">
					<el-switch v-model="form.must" />
					<span class="field-hint">开启后报价页该维度默认选中首项</span>
				</el-form-item>
				<el-form-item v-if="Number(form.pid) > 0" label="计价类型">
					<el-radio-group v-model="form.type" @change="onTypeChange">
						<el-radio :label="0">无（仅选择）</el-radio>
						<el-radio :label="1">前置</el-radio>
						<el-radio :label="2">后置</el-radio>
						<el-radio :label="3">工艺</el-radio>
					</el-radio-group>
					<p class="field-hint field-hint--block">无：只作为商品选项，不参与加价</p>
				</el-form-item>
				<el-form-item v-if="isAddPriceType" label="加价">
					<el-input-number v-model="form.price" :min="0" :precision="2" style="width: 100%" />
					<span class="field-hint">前置跟尺寸走，后置按件加一笔</span>
				</el-form-item>
				<el-form-item v-if="isCraftType" label="倍率">
					<el-input-number v-model="form.price" :min="0.01" :precision="2" :step="0.1" style="width: 100%" />
					<span class="field-hint">1.2 表示单价上浮 20%</span>
				</el-form-item>
				<el-form-item label="备注">
					<el-input v-model="form.remarks" placeholder="可选，用于下拉展示补充说明" />
				</el-form-item>
				<el-form-item label="排序">
					<el-input-number v-model="form.weigh" :min="0" style="width: 100%" />
					<span class="field-hint">越大越靠前</span>
				</el-form-item>
			</el-form>
			<div slot="footer">
				<el-button @click="formVisible = false">取消</el-button>
				<el-button type="primary" :loading="saving" @click="saveForm">保存</el-button>
			</div>
		</el-dialog>
	</section>
</template>

<script>
export default {
	name: 'ParameterManage',
	props: {
		canAdd: { type: Boolean, default: false },
		canEdit: { type: Boolean, default: false },
		canDel: { type: Boolean, default: false },
	},
	data() {
		return {
			loading: false,
			saving: false,
			search: '',
			list: [],
			parentOptions: [],
			formVisible: false,
			form: this.emptyForm(0),
			formRules: { name: [{ required: true, message: '请输入名称', trigger: 'blur' }] },
		};
	},
	computed: {
		comboExample() {
			const sample = this.list.find((r) => Number(r.pid || 0) === 0 && String(r.combo_example || '').trim());
			return sample ? String(sample.combo_example) : '';
		},
		dialogTitle() {
			if (this.form.id) return '编辑参数';
			return this.form.pid ? '添加选项' : '添加维度';
		},
		isAddPriceType() {
			const type = Number(this.form.type);
			return Number(this.form.pid) > 0 && (type === 1 || type === 2);
		},
		isCraftType() {
			return Number(this.form.pid) > 0 && Number(this.form.type) === 3;
		},
		visibleRows() {
			const q = String(this.search || '').trim().toLowerCase();
			if (!q) return this.list;
			const matched = {};
			this.list.forEach((r) => {
				const name = String(r.name || r.name_display || '').toLowerCase();
				if (name.indexOf(q) >= 0) {
					matched[r.id] = true;
					const pid = Number(r.pid || 0);
					if (pid) matched[pid] = true;
				}
			});
			this.list.forEach((r) => {
				if (matched[Number(r.pid || 0)]) matched[r.id] = true;
			});
			return this.list.filter((r) => matched[r.id]);
		},
		treeList() {
			return buildParameterTree(this.visibleRows);
		},
	},
	created() {
		this.loadParents();
		this.getList();
	},
	methods: {
		emptyForm(pid) {
			return { id: 0, name: '', pid: pid || 0, price: 0, remarks: '', type: 0, must: false, craft: false, weigh: 0 };
		},
		resolveType(row) {
			if (this.truthy(row && row.craft)) return 3;
			if (row && (row.type === true || row.type === '1')) return 1;
			const type = Number(row && row.type);
			return type === 1 || type === 2 || type === 3 ? type : 0;
		},
		onTypeChange(type) {
			const next = Number(type);
			if (next === 0) this.form.price = 0;
			if (next === 3 && Number(this.form.price) < 1) this.form.price = 1;
		},
		formatPriceCell(row) {
			if (Number(row.pid || 0) === 0) return '—';
			const type = this.resolveType(row);
			const n = Number(row.price);
			if (type === 3) return '×' + (Number.isFinite(n) ? n.toFixed(2) : '1.00');
			if (type === 1 || type === 2) return Number.isFinite(n) ? n.toFixed(2) : '—';
			return '—';
		},
		formatTime(ts) {
			const n = Number(ts);
			if (!n) return '—';
			const d = new Date(n * 1000);
			const pad = (v) => String(v).padStart(2, '0');
			return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
		},
		formatMoney(v) {
			const n = Number(v);
			return Number.isFinite(n) ? n.toFixed(2) : '—';
		},
		truthy(v) {
			return v === true || v === 1 || v === '1';
		},
		api(action, params) {
			return this.$fetch('addons/hlm/api.FrontAdmin/' + action, Object.assign({ resource: 'parameter' }, params || {}));
		},
		post(action, params) {
			return this.$post('addons/hlm/api.FrontAdmin/' + action, Object.assign({ resource: 'parameter' }, params || {}));
		},
		loadParents() {
			const that = this;
			that.api('manageMeta', { action: 'parentOptions' }).then(function(res) {
				if (res.code === 1) that.parentOptions = res.data || [];
			});
		},
		getList() {
			const that = this;
			that.loading = true;
			that.api('manageList', { page: 1, limit: 500 }).then(function(res) {
				if (res.code === 1) that.list = res.data.list || [];
				that.$nextTick(function() { that.setExpandAll(true); });
			}).finally(function() {
				that.loading = false;
			});
		},
		onSearch() {
			this.$nextTick(() => this.setExpandAll(true));
		},
		treeRowClass({ row }) {
			const pid = Number(row.pid || 0);
			if (!pid) return 'is-tree-root';
			const parent = findTreeNode(this.treeList, pid);
			const kids = parent && parent.children ? parent.children : [];
			const last = kids[kids.length - 1];
			return last && Number(last.id) === Number(row.id) ? 'is-tree-leaf is-tree-last' : 'is-tree-leaf';
		},
		setExpandAll(expand) {
			const table = this.$refs.paramTable;
			if (!table) return;
			const walk = function(nodes) {
				(nodes || []).forEach(function(row) {
					if (row.children && row.children.length) {
						table.toggleRowExpansion(row, expand);
						walk(row.children);
					}
				});
			};
			walk(this.treeList);
		},
		openCreate(pid) {
			this.form = this.emptyForm(pid || 0);
			this.formVisible = true;
		},
		openEdit(id) {
			const that = this;
			that.api('manageDetail', { id }).then(function(res) {
				if (res.code !== 1) return;
				const row = res.data || {};
				const type = that.resolveType(row);
				that.form = {
					id: Number(row.id || 0),
					name: String(row.name || ''),
					pid: Number(row.pid || 0),
					price: type === 3 && Number(row.price || 0) <= 0 ? 1 : Number(row.price || 0),
					remarks: String(row.remarks || ''),
					type,
					must: that.truthy(row.must),
					craft: type === 3,
					weigh: Number(row.weigh || 0),
				};
				that.formVisible = true;
			});
		},
		resetForm() {
			this.form = this.emptyForm(0);
		},
		saveForm() {
			const that = this;
			that.$refs.paramForm.validate(function(valid) {
				if (!valid) return;
				that.saving = true;
				const type = Number(that.form.pid) > 0 ? Number(that.form.type) || 0 : 0;
				that.post('manageSave', Object.assign({}, that.form, {
					type,
					craft: type === 3,
					price: type === 0 ? 0 : that.form.price,
				})).then(function(res) {
					if (res.code === 1) {
						that.$message.success(res.msg || '保存成功');
						that.formVisible = false;
						that.loadParents();
						that.getList();
					}
				}).finally(function() {
					that.saving = false;
				});
			});
		},
		delRow(id) {
			const that = this;
			that.api('manageDel', { ids: String(id) }).then(function(res) {
				if (res.code === 1) {
					that.$message.success('已删除');
					that.loadParents();
					that.getList();
				}
			});
		},
	},
};

function buildParameterTree(rows) {
	const list = Array.isArray(rows) ? rows : [];
	const map = {};
	const roots = [];
	list.forEach((r) => {
		map[r.id] = Object.assign({}, r, { children: [] });
	});
	list.forEach((r) => {
		const node = map[r.id];
		const pid = Number(r.pid || 0);
		if (pid && map[pid]) map[pid].children.push(node);
		else roots.push(node);
	});
	const prune = function(nodes) {
		nodes.forEach((n) => {
			if (n.children && n.children.length) prune(n.children);
			else delete n.children;
		});
	};
	prune(roots);
	return roots;
}

function findTreeNode(nodes, id) {
	const list = nodes || [];
	for (let i = 0; i < list.length; i++) {
		const n = list[i];
		if (Number(n.id) === Number(id)) return n;
		if (n.children && n.children.length) {
			const hit = findTreeNode(n.children, id);
			if (hit) return hit;
		}
	}
	return null;
}
</script>

<style scoped>
.parameter-manage >>> .pm-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 12px;
	padding: 16px 20px;
	border-bottom: 1px solid var(--app-border, #e5e7eb);
}
.parameter-manage >>> .pm-search { width: 260px; }
.parameter-manage >>> .row-actions {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 2px;
}
.parameter-manage >>> .row-action-btn { color: var(--app-primary); }
.parameter-manage >>> .row-action-btn--danger { color: #ef4444; }
.parameter-manage >>> .pm-header-cell {
	background: #f9fafb !important;
	font-size: 13px;
	font-weight: 600;
}
.muted { color: var(--app-text-muted, #9ca3af); }
.field-hint { margin-left: 8px; font-size: 12px; color: var(--app-text-secondary); }
.field-hint--block { display: block; margin: 6px 0 0; }
.pm-time { font-size: 12px; color: var(--app-text-secondary); }
.tree-name { font-size: 13px; }
.tree-name.is-root { font-weight: 600; }

.parameter-manage >>> .pm-tree-table .el-table__body td:first-child,
.parameter-manage >>> .pm-tree-table .el-table__body td:first-child .cell {
	overflow: visible;
}
.parameter-manage >>> .pm-tree-table .el-table__indent {
	position: relative;
	display: inline-block;
	height: 1em;
	vertical-align: middle;
}
.parameter-manage >>> .pm-tree-table .el-table__placeholder {
	position: relative;
	display: inline-block;
	width: 16px;
	height: 1em;
	vertical-align: middle;
}
.parameter-manage >>> .pm-tree-table .is-tree-leaf .el-table__indent::before {
	content: '';
	position: absolute;
	left: 9px;
	top: -28px;
	bottom: -28px;
	border-left: 1px solid #d1d5db;
}
.parameter-manage >>> .pm-tree-table .is-tree-last .el-table__indent::before {
	bottom: 50%;
}
.parameter-manage >>> .pm-tree-table .is-tree-leaf .el-table__placeholder::before {
	content: '';
	position: absolute;
	left: 0;
	top: 50%;
	width: 12px;
	border-top: 1px solid #d1d5db;
}
.parameter-manage >>> .pm-tree-table .el-table__expand-icon {
	color: var(--app-primary, #45b299);
}
.parameter-manage >>> .pm-tree-table .el-table__expand-icon .el-icon-arrow-right {
	font-weight: 700;
}
.parameter-manage >>> .pm-tree-table .el-table__placeholder + .el-table__placeholder {
	display: none;
}
</style>
<style src="../../assets/css/front-admin.css"></style>
